import React, { useMemo, useEffect, useRef, useState } from 'react';
import { isSameDay, parseISO } from 'date-fns';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import TimeAxis from './TimeAxis';
import DayColumn from './DayColumn';
import { START_HOUR, END_HOUR } from './positioningEngine';
import type { Appointment, ClinicRoom } from '../../../services/clinicsService';
import type { ClinicSchedule, ClinicHoliday } from '../../../services/clinicSchedulesService';

interface WeeklyResourceGridProps {
  daysToRender: Date[];
  rooms: ClinicRoom[];
  appointments: Appointment[];
  schedules: ClinicSchedule[];
  holidays: ClinicHoliday[];
  onSlotClick?: (date: Date, room: ClinicRoom) => void;
  onAppointmentClick?: (app: Appointment, e: React.MouseEvent) => void;
  onRoomClick?: (room: ClinicRoom) => void;
}

export default function WeeklyResourceGrid({
  daysToRender,
  rooms,
  appointments,
  schedules,
  holidays,
  onSlotClick,
  onAppointmentClick,
  onRoomClick
}: WeeklyResourceGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);

  const timeSlots = useMemo(() => {
    const slots = [];
    for (let h = START_HOUR; h < END_HOUR; h++) {
      slots.push(`${h.toString().padStart(2, '0')}:00`);
      slots.push(`${h.toString().padStart(2, '0')}:30`);
    }
    return slots;
  }, []);

  const getDaySchedule = (date: Date) => {
    const holiday = holidays.find(h => isSameDay(parseISO(h.fecha), date));
    if (holiday) return { isHoliday: true, isOpen: holiday.esta_abierto, holiday };
    
    const dayStr = format(date, 'eeee', { locale: es }).toLowerCase();
    const normalizedDayStr = dayStr.replace('é', 'e').replace('á', 'a');
    
    const daySchedules = schedules.filter(s => {
      const sDay = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'][s.day_of_week];
      return sDay === normalizedDayStr;
    });
    
    return { isHoliday: false, isOpen: daySchedules.length > 0, schedules: daySchedules };
  };

  const isSlotAvailable = (timeStr: string, daySched: any) => {
    if (daySched.isHoliday && !daySched.isOpen) return false;
    
    const [h, m] = timeStr.split(':').map(Number);
    const slotTime = h * 60 + m;
    
    if (daySched.isHoliday && daySched.isOpen) {
      if (daySched.holiday?.hora_apertura_especial && daySched.holiday?.hora_cierre_especial) {
         const [oh, om] = daySched.holiday.hora_apertura_especial.split(':').map(Number);
         const [ch, cm] = daySched.holiday.hora_cierre_especial.split(':').map(Number);
         return slotTime >= (oh * 60 + om) && slotTime < (ch * 60 + cm);
      }
      return false;
    }

    return daySched.schedules.some((s: ClinicSchedule) => {
      const [oh, om] = s.start_time.split(':').map(Number);
      const [ch, cm] = s.end_time.split(':').map(Number);
      return slotTime >= (oh * 60 + om) && slotTime < (ch * 60 + cm);
    });
  };

  useEffect(() => {
    // Wait until we have schedules to determine opening hours
    if (schedules.length === 0 && holidays.length === 0) return;
    if (daysToRender.length === 0) return;

    // Only auto-scroll once per component mount / data load
    if (hasAutoScrolled) return;

    let earliestSlotTime = 24 * 60; // Max time in minutes

    daysToRender.forEach(day => {
      const daySched = getDaySchedule(day);
      
      if (daySched.isHoliday && daySched.isOpen && daySched.holiday?.hora_apertura_especial) {
         const [h, m] = daySched.holiday.hora_apertura_especial.split(':').map(Number);
         earliestSlotTime = Math.min(earliestSlotTime, h * 60 + m);
      } else if (!daySched.isHoliday && daySched.schedules && daySched.schedules.length > 0) {
         daySched.schedules.forEach((s: ClinicSchedule) => {
           const [h, m] = s.start_time.split(':').map(Number);
           earliestSlotTime = Math.min(earliestSlotTime, h * 60 + m);
         });
      }
    });

    if (earliestSlotTime < 24 * 60 && containerRef.current) {
      const offsetMins = earliestSlotTime - (START_HOUR * 60);
      const yOffset = (offsetMins / 30) * 48; // 48 is the pixel height of a 30-min slot
      
      // We subtract 88px to account for the sticky header (Day + Room headers),
      // and another 48px (1 slot) to give some breathing room above the opening hour.
      const scrollPosition = Math.max(0, yOffset - 88 - 48); 

      // Small timeout to ensure rendering has completed
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTo({ top: scrollPosition, behavior: 'smooth' });
          setHasAutoScrolled(true);
        }
      }, 100);
    }
  }, [daysToRender, schedules, holidays, hasAutoScrolled]);

  return (
    <div data-ui="weekly-resource-grid-region-001"
      ref={containerRef}
      className="flex-1 overflow-x-auto overflow-y-auto bg-black/20 custom-scrollbar relative"
    >
      <div data-ui="weekly-resource-grid-region-002" className="flex min-w-max min-h-max">
        <TimeAxis timeSlots={timeSlots} />
        
        <div data-ui="weekly-resource-grid-region-003" className="flex-1 flex min-w-max">
        {daysToRender.map((day, i) => {
          const daySched = getDaySchedule(day);
          const isToday = isSameDay(day, new Date());
          const dayApps = appointments.filter(app => {
            const start = parseISO(app.start_time);
            return isSameDay(start, day);
          });

          return (
            <DayColumn
              key={i}
              date={day}
              rooms={rooms}
              appointments={dayApps}
              timeSlots={timeSlots}
              daySched={daySched}
              isToday={isToday}
              isSlotAvailable={(timeStr) => isSlotAvailable(timeStr, daySched)}
              onSlotClick={onSlotClick}
              onAppointmentClick={onAppointmentClick}
              onRoomClick={onRoomClick}
            />
          );
        })}
        </div>
      </div>
    </div>
  );
}

